import { motion } from 'framer-motion';
import { useEffect,useState } from 'react';
import { Link } from 'react-router';

import { strapiService } from '../../services/strapiService';
import type { Article } from '../../types/article';
import OptimizedImage from '../OptimizedImage';

const BlogSection = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRecentArticles = async () => {
      try {
        setLoading(true);
        const data = await strapiService.getArticles();
        // Get only the 3 most recent articles
        const recentArticles = data
          .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
          .slice(0, 3);
        setArticles(recentArticles);
        setError(null);
      } catch (err) {
        setError('Failed to load articles.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentArticles();
  }, []);

  if (loading) {
    return (
      <section className="section bg-card dark:bg-panel/95">
        <div className="container-custom">
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-secondary"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error || articles.length === 0) {
    return null; // Don't show section if there's an error or no articles
  }

  return (
    <section id="blog" className="section bg-card dark:bg-panel/95">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-primary dark:text-theme-primary mb-4">
            featured wisdom
          </h2>
          <div className="w-24 h-1 bg-brand-secondary mx-auto mb-6"></div>
          <p className="text-lg text-theme-secondary max-w-3xl mx-auto">
            simple, helpful insights to support your everyday relationships.
          </p>
        </motion.div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {articles.map((article, index) => (
            <motion.article
              key={article.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-surface dark:bg-panel rounded-xl shadow-surface overflow-hidden hover:shadow-xl transition-all duration-300 group"
            >
              <Link to={`/wisdom/${article.slug}`} className="block">
                {/* Cover Image */}
                <div className="relative h-48 overflow-hidden bg-panel">
                  {article.cover ? (
                    <OptimizedImage
                      src={strapiService.getMediaUrl(article.cover.url)}
                      alt={article.cover.alternativeText || article.title}
                      className="group-hover:scale-105 transition-transform duration-300"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                  ) : (
                    <div className="w-full h-full bg-panel flex items-center justify-center">
                      <span className="text-theme-secondary">No image</span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Title */}
                  <h3 className="text-xl font-bold text-primary dark:text-theme-primary mb-2 line-clamp-2 group-hover:text-brand-primary dark:group-hover:text-brand-secondary transition-colors">
                    {article.title}
                  </h3>

                  {/* Description */}
                  <p className="text-theme-secondary mb-4 line-clamp-3">
                    {article.description}
                  </p>

                  {/* Author & Date */}
                  <div className="flex items-center justify-between text-sm text-theme-secondary">
                    {article.author && (
                      <span className="font-medium">{article.author.name}</span>
                    )}
                    <time dateTime={article.publishedAt}>
                      {new Date(article.publishedAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </time>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <Link
            to="/wisdom"
            className="btn-primary inline-block"
          >
            View All Articles
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;
